import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import NavBar from "@/components/NavBar";
import { useAuth } from "@/lib/auth";
import { Skull, Crown, Loader, ChevronLeft } from "lucide-react";

/**
 * Hall of Hordes — all-time biggest hordes ever recorded.
 * Lives at /hall. Public-readable (no auth needed).
 *
 * Aesthetic: stone-tablet, etched. Sets it apart from the weekly leaderboard
 * which is a "moment in time" snapshot. The Hall is permanent record.
 */
export default function Hall() {
  const { auth } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard/all_time")
       .then(({ data }) => setData(data))
       .catch(() => setData({ hall: [], size: 20 }))
       .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen relative">
      {auth ? (
        <NavBar />
      ) : (
        <nav className="relative z-20 border-b border-subtle bg-void/95 backdrop-blur">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-12 py-5 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="hall-back-link">
              <ChevronLeft className="w-4 h-4 text-blood" />
              <span className="tac-label">RETURN TO THE DIRT</span>
            </Link>
            <Link to="/auth" className="btn-ghost" data-testid="hall-enter-link">ENTER ↗</Link>
          </div>
        </nav>
      )}

      {/* Stone-tablet backdrop */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-25"
           style={{
             background: "radial-gradient(ellipse at top, #2a1010 0%, transparent 50%)",
           }} />

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 lg:py-20 relative z-10">
        <div className="text-center mb-10 fade-up">
          <p className="tac-label mb-4">// PERMANENT RECORD //</p>
          <h1 className="font-serif-h text-5xl sm:text-6xl text-bone uppercase leading-none"
              data-testid="hall-title">
            THE HALL OF<br />
            <span className="text-blood">HORDES</span>
          </h1>
          <p className="text-sm text-muted-h mt-6 max-w-md mx-auto leading-relaxed italic">
            The largest hordes ever assembled. These numbers do not reset.
            Etched the moment they were reached. The dead remember.
          </p>
        </div>

        {loading && (
          <div className="text-center py-20" data-testid="hall-loading">
            <Loader className="w-4 h-4 animate-spin inline text-blood" />
            <p className="tac-label mt-4">EXHUMING THE RECORD</p>
          </div>
        )}

        {!loading && data?.hall?.length === 0 && (
          <div className="text-center py-20" data-testid="hall-empty">
            <Skull className="w-8 h-8 text-blood mx-auto" strokeWidth={1} />
            <p className="font-serif-h text-2xl text-bone mt-4">EMPTY</p>
            <p className="text-sm text-muted-h mt-2">No horde has yet been counted.</p>
          </div>
        )}

        {!loading && data?.hall?.length > 0 && (
          <div className="narrative-panel"
               style={{ borderColor: "#3a2a1a", borderWidth: 1 }}
               data-testid="hall-panel">
            <ol className="space-y-2" data-testid="hall-list">
              {data.hall.map((row) => (
                <HallRow key={row.user_id} row={row} />
              ))}
            </ol>
          </div>
        )}

        <p className="text-center mt-8 text-[0.7rem] text-muted-h tracking-widest">
          PEAK HORDE = LARGEST EVER COMMANDED · DOES NOT RESET
        </p>
      </div>
    </div>
  );
}

const HallRow = ({ row }) => {
  const podium = row.position <= 3;
  const isAI = row.is_ai;
  return (
    <li className={`flex items-center gap-4 px-3 py-3 transition-colors border-l-2 ${
          row.position === 1 ? "border-blood bg-[#1a0606]" :
          podium ? "border-[#5e4a3a]" :
          isAI ? "border-[#3a1a1a]" : "border-transparent"
        }`}
        data-testid={`hall-row-${row.position}`}>
      <span className={`font-serif-h text-3xl w-10 text-right ${
        row.position === 1 ? "text-blood" :
        podium ? "text-bone" : "text-muted-h"
      }`}>
        {row.position}
      </span>
      <div className="flex-1 min-w-0">
        <p className={`font-serif-h text-xl truncate ${
          isAI ? "text-[#a89c7d] italic" : "text-bone"
        }`} data-testid={`hall-callsign-${row.position}`}>
          {row.callsign}
          {isAI && <span className="text-[#5e4a3a] ml-2 text-[0.65rem] not-italic font-mono">— LEGEND</span>}
        </p>
        <p className="font-mono text-[0.65rem] text-muted-h tracking-wider mt-1">
          {row.rank_name?.toUpperCase()}
          {!isAI && ` · ${row.battles_won} WON`}
          {!isAI && ` · ${row.humans_converted?.toLocaleString()} CONVERTED`}
        </p>
      </div>
      <div className="text-right">
        <p className={`font-serif-h text-2xl tabular-nums ${
          isAI ? "text-[#a89c7d]" : "text-bone"
        }`} data-testid={`hall-peak-${row.position}`}>
          {row.peak_horde.toLocaleString()}
        </p>
        {!isAI && row.current_horde !== row.peak_horde && (
          <p className="text-[0.6rem] font-mono text-muted-h tracking-wider mt-0.5">
            NOW · {row.current_horde.toLocaleString()}
          </p>
        )}
      </div>
      {row.position === 1 && <Crown className="w-4 h-4 text-blood ml-1" />}
    </li>
  );
};
